import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faStore, faHeadset, faGraduationCap, faCalendarAlt, faShoppingCart, faUserPlus, faCog, faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import ChatbotAPI from './ChatbotAPI';
import { ErrorToastContainer, SuccessToastContainer } from '../../../function/CustomFunction';

export default function ChatbotTemplates({ onTemplateSelect, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Pre-built templates
  const templateCategories = [
    { id: 'all', label: 'All Templates', icon: faRocket },
    { id: 'customer-support', label: 'Customer Support', icon: faHeadset },
    { id: 'ecommerce', label: 'E-commerce', icon: faStore },
    { id: 'education', label: 'Education', icon: faGraduationCap },
    { id: 'appointment', label: 'Appointments', icon: faCalendarAlt },
    { id: 'lead-generation', label: 'Lead Generation', icon: faUserPlus },
    { id: 'product-catalog', label: 'Product Catalog', icon: faShoppingCart },
    { id: 'technical-support', label: 'Technical Support', icon: faCog }
  ];

  const preBuiltTemplates = [
    {
      id: 'customer-support-basic',
      name: 'Basic Customer Support',
      description: 'Handle common customer inquiries with automated responses and escalation to human agents.',
      category: 'customer-support',
      icon: '🎧',
      features: ['FAQ Responses', 'Ticket Creation', 'Agent Escalation', 'Satisfaction Survey'],
      nodes: [
        {
          id: 'welcome',
          type: 'text_button',
          data: {
            message: 'Hello! I\'m here to help you with any questions or issues. How can I assist you today?',
            buttons: [
              { label: 'Account Help', target: 'account' },
              { label: 'Technical Issue', target: 'technical' },
              { label: 'Billing Question', target: 'billing' },
              { label: 'Speak to Agent', target: 'agent' }
            ]
          }
        },
        {
          id: 'account',
          type: 'text_button',
          data: {
            message: 'I can help you with account-related questions. What do you need assistance with?',
            buttons: [
              { label: 'Password Reset', target: 'password-reset' },
              { label: 'Update Profile', target: 'profile-update' },
              { label: 'Account Settings', target: 'settings' },
              { label: 'Back to Main Menu', target: 'welcome' }
            ]
          }
        },
        {
          id: 'technical',
          type: 'text_button',
          data: {
            message: 'Let me help you with technical issues. Please describe the problem you\'re experiencing.',
            buttons: [
              { label: 'App Not Working', target: 'app-issue' },
              { label: 'Login Problems', target: 'login-issue' },
              { label: 'Performance Issues', target: 'performance' },
              { label: 'Back to Main Menu', target: 'welcome' }
            ]
          }
        },
        {
          id: 'agent',
          type: 'text_button',
          data: {
            message: 'I\'m connecting you with a human agent. Please wait while I transfer you...',
            buttons: [
              { label: 'Cancel Transfer', target: 'welcome' }
            ]
          }
        }
      ]
    },
    {
      id: 'ecommerce-store',
      name: 'E-commerce Store Assistant',
      description: 'Help customers browse products, check availability, and process orders.',
      category: 'ecommerce',
      icon: '🛍️',
      features: ['Product Search', 'Order Tracking', 'Price Inquiries', 'Cart Management'],
      nodes: [
        {
          id: 'welcome',
          type: 'text_button',
          data: {
            message: 'Welcome to our store! I can help you find products, check orders, or answer any questions.',
            buttons: [
              { label: 'Browse Products', target: 'products' },
              { label: 'Track Order', target: 'track-order' },
              { label: 'Check Prices', target: 'pricing' },
              { label: 'Customer Service', target: 'support' }
            ]
          }
        },
        {
          id: 'products',
          type: 'list',
          data: {
            message: 'What type of products are you looking for?',
            listTitle: 'Product Categories',
            listOptions: [
              { id: 'electronics', title: 'Electronics', description: 'Phones, laptops, gadgets' },
              { id: 'clothing', title: 'Clothing', description: 'Men\'s and women\'s fashion' },
              { id: 'home', title: 'Home & Garden', description: 'Furniture and decor' },
              { id: 'sports', title: 'Sports & Fitness', description: 'Equipment and apparel' }
            ]
          }
        },
        {
          id: 'track-order',
          type: 'user_input',
          data: {
            prompt: 'Please enter your order number to track your package:',
            variableName: 'order_number',
            inputType: 'text',
            validation: {
              required: true,
              minLength: 6,
              errorMessage: 'Please enter a valid order number (minimum 6 characters).'
            }
          }
        }
      ]
    },
    {
      id: 'lead-generation',
      name: 'Lead Generation Bot',
      description: 'Capture leads, qualify prospects, and schedule follow-up meetings.',
      category: 'lead-generation',
      icon: '🎯',
      features: ['Lead Capture', 'Qualification Questions', 'Meeting Scheduler', 'Follow-up Automation'],
      nodes: [
        {
          id: 'welcome',
          type: 'text_button',
          data: {
            message: 'Hi! I\'m here to help you learn more about our services. Are you interested in a consultation?',
            buttons: [
              { label: 'Yes, I\'m interested', target: 'qualification' },
              { label: 'Just browsing', target: 'info' },
              { label: 'Schedule a call', target: 'schedule' }
            ]
          }
        },
        {
          id: 'qualification',
          type: 'user_input',
          data: {
            prompt: 'Great! What\'s your name and company?',
            variableName: 'contact_info',
            inputType: 'text',
            validation: {
              required: true,
              minLength: 5,
              errorMessage: 'Please provide your name and company.'
            }
          }
        },
        {
          id: 'schedule',
          type: 'text_button',
          data: {
            message: 'Perfect! When would be a good time for a 15-minute call?',
            buttons: [
              { label: 'Today', target: 'today-schedule' },
              { label: 'This Week', target: 'week-schedule' },
              { label: 'Next Week', target: 'next-week-schedule' }
            ]
          }
        }
      ]
    },
    {
      id: 'appointment-booking',
      name: 'Appointment Booking',
      description: 'Allow customers to book appointments, check availability, and manage bookings.',
      category: 'appointment',
      icon: '📅',
      features: ['Availability Check', 'Appointment Booking', 'Rescheduling', 'Reminders'],
      nodes: [
        {
          id: 'welcome',
          type: 'text_button',
          data: {
            message: 'Welcome! I can help you book an appointment. What service do you need?',
            buttons: [
              { label: 'Consultation', target: 'consultation' },
              { label: 'Service Appointment', target: 'service' },
              { label: 'Check Availability', target: 'availability' },
              { label: 'Reschedule', target: 'reschedule' }
            ]
          }
        },
        {
          id: 'consultation',
          type: 'text_button',
          data: {
            message: 'Great! I have consultation slots available. When would you prefer?',
            buttons: [
              { label: 'Morning (9-12)', target: 'morning-slots' },
              { label: 'Afternoon (1-5)', target: 'afternoon-slots' },
              { label: 'Evening (6-8)', target: 'evening-slots' }
            ]
          }
        },
        {
          id: 'morning-slots',
          type: 'text_button',
          data: {
            message: 'Available morning slots:',
            buttons: [
              { label: '9:00 AM', target: 'confirm-9am' },
              { label: '10:30 AM', target: 'confirm-1030am' },
              { label: '11:00 AM', target: 'confirm-11am' }
            ]
          }
        }
      ]
    },
    {
      id: 'education-course',
      name: 'Course Information Bot',
      description: 'Provide course information, enrollment details, and student support.',
      category: 'education',
      icon: '🎓',
      features: ['Course Catalog', 'Enrollment Process', 'Student Support', 'Progress Tracking'],
      nodes: [
        {
          id: 'welcome',
          type: 'text_button',
          data: {
            message: 'Welcome to our learning platform! How can I help you today?',
            buttons: [
              { label: 'Browse Courses', target: 'courses' },
              { label: 'Enroll Now', target: 'enrollment' },
              { label: 'Student Support', target: 'support' },
              { label: 'Check Progress', target: 'progress' }
            ]
          }
        },
        {
          id: 'courses',
          type: 'list',
          data: {
            message: 'Here are our available courses:',
            listTitle: 'Course Categories',
            listOptions: [
              { id: 'programming', title: 'Programming', description: 'Learn coding languages' },
              { id: 'design', title: 'Design', description: 'UI/UX and graphic design' },
              { id: 'business', title: 'Business', description: 'Management and entrepreneurship' },
              { id: 'marketing', title: 'Marketing', description: 'Digital marketing strategies' }
            ]
          }
        }
      ]
    },
    {
      id: 'technical-support',
      name: 'Technical Support Bot',
      description: 'Provide technical assistance, troubleshooting guides, and issue resolution.',
      category: 'technical-support',
      icon: '🔧',
      features: ['Troubleshooting', 'Error Resolution', 'System Status', 'Expert Escalation'],
      nodes: [
        {
          id: 'welcome',
          type: 'text_button',
          data: {
            message: 'Technical Support here! What issue are you experiencing?',
            buttons: [
              { label: 'System Error', target: 'system-error' },
              { label: 'Performance Issue', target: 'performance' },
              { label: 'Integration Problem', target: 'integration' },
              { label: 'Account Access', target: 'access' }
            ]
          }
        },
        {
          id: 'system-error',
          type: 'text_button',
          data: {
            message: 'Let me help you resolve the system error. What error message are you seeing?',
            buttons: [
              { label: 'Error Code 500', target: 'error-500' },
              { label: 'Connection Failed', target: 'connection-error' },
              { label: 'Login Failed', target: 'login-error' },
              { label: 'Other Error', target: 'other-error' }
            ]
          }
        }
      ]
    }
  ];

  useEffect(() => {
    setTemplates(preBuiltTemplates);
  }, []);

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(template => template.category === selectedCategory);

  const handleTemplateSelect = (template) => {
    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
  };

  const handleUseTemplate = async (template) => {
    try {
      setLoading(true);
      
      // Convert template to chatbot format
      const chatbotData = {
        isactive: 1,
        chatbot_name: template.name,
        tgm: ['start', 'hello', 'hi'],
        chatbot_node: {
          'start': template.nodes.map((node, index) => ({
            node_id: index + 1,
            next_node_id: index < template.nodes.length - 1 ? index + 2 : null,
            type: node.type,
            message: node.data.message || '',
            delay: 0,
            ...node.data
          }))
        }
      };

      // Create chatbot using API
      const result = await ChatbotAPI.createChatbot(chatbotData);
      
      if (result) {
        SuccessToastContainer(`Template "${template.name}" created successfully!`);
        if (onClose) onClose();
      }
      
    } catch (error) {
      console.error('Error creating template:', error);
      ErrorToastContainer('Failed to create chatbot from template');
    } finally {
      setLoading(false);
    }
  };

  const TemplateCard = ({ template }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid #e0e0e0',
      transition: 'all 0.2s',
      cursor: 'pointer'
    }}
    onMouseOver={e => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
    }}
    onMouseOut={e => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ fontSize: '32px', marginRight: '12px' }}>{template.icon}</span>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
            {template.name}
          </h3>
          <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {templateCategories.find(cat => cat.id === template.category)?.label}
          </div>
        </div>
      </div>
      
      <p style={{ 
        margin: '0 0 16px 0', 
        fontSize: '14px', 
        color: '#666', 
        lineHeight: '1.5' 
      }}>
        {template.description}
      </p>
      
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px', fontWeight: '500' }}>
          KEY FEATURES:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {template.features.map((feature, index) => (
            <span key={index} style={{
              background: '#e8f5e8',
              color: '#075E54',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '500'
            }}>
              {feature}
            </span>
          ))}
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => handlePreview(template)}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            color: '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <FontAwesomeIcon icon={faEye} />
          Preview
        </button>
        <button
          onClick={() => handleUseTemplate(template)}
          disabled={loading}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: '#075E54',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            opacity: loading ? 0.6 : 1
          }}
        >
          <FontAwesomeIcon icon={faDownload} />
          {loading ? 'Creating...' : 'Use Template'}
        </button>
      </div>
    </div>
  );

  const PreviewModal = ({ template, onClose }) => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            {template.icon} {template.name}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>DESCRIPTION</h4>
          <p style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: '1.5' }}>
            {template.description}
          </p>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>FLOW STRUCTURE</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {template.nodes.map((node, index) => (
              <div key={index} style={{
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                  {index + 1}. {node.type.replace('_', ' ').toUpperCase()}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {node.data.message || 'Interactive element'}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              background: '#f8f9fa',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: '#666'
            }}
          >
            Close
          </button>
          <button
            onClick={() => {
              handleUseTemplate(template);
              onClose();
            }}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px',
              background: '#075E54',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Creating...' : 'Use This Template'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '1000px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>
              🤖 Chatbot Templates
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
              Choose from pre-built templates to get started quickly
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Category Filter */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          overflowX: 'auto',
          paddingBottom: '8px'
        }}>
          {templateCategories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                borderRadius: '20px',
                border: 'none',
                background: selectedCategory === category.id ? '#075E54' : '#f0f0f0',
                color: selectedCategory === category.id ? 'white' : '#333',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
            >
              <FontAwesomeIcon icon={category.icon} />
              <span>{category.label}</span>
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '20px'
        }}>
          {filteredTemplates.map(template => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>

        {filteredTemplates.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <div style={{ fontSize: '16px', fontWeight: '500' }}>No templates found</div>
            <div style={{ fontSize: '14px' }}>Try selecting a different category</div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewTemplate && (
        <PreviewModal 
          template={previewTemplate} 
          onClose={() => setPreviewTemplate(null)} 
        />
      )}
    </div>
  );
}

